import { type Quill as QuillType, type StringMap } from 'quill';
import { type MutableRefObject, type RefObject } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { type EmojiPickerProps } from '@hivebrite/design-system/EmojiPicker';

import { type QuillEditorOptions } from 'hb-react/shared/components/DesignSystem/QuillEditor/types';
import { type GlobalStorageFile } from 'hb-react/shared/hooks/useGlobalStorageV2/types';

interface QuillEditorContextProps {
  acceptAllFiles?: boolean;
  attachments?: Array<GlobalStorageFile>;
  compact?: boolean;
  displayLinkModal?: boolean;
  displayVideoModal?: boolean;
  editorWrapperRef?: RefObject<HTMLDivElement>;
  files?: Array<GlobalStorageFile>;
  format?: StringMap | null;
  id?: string;
  images?: Array<GlobalStorageFile>;
  initialEmbed?: object | null;
  isEditorFocus?: boolean;
  offEditorChange?: (fn: Function) => void;
  onAddEmbed?: (embed: any) => void;
  onAddFiles?: ({
    type,
    files,
    keys,
  }: {
    files: Array<File>;
    keys?: Array<string>;
    type: 'attachment' | 'image' | 'video';
  }) => void;
  onAddLink?: (string) => void;
  onEditorChange?: (fn: Function) => void;
  onEditorEventsRef?: MutableRefObject<Array<Function>>;
  onEmojiClick?: EmojiPickerProps['onEmojiPick'];
  onRemoveEmbed?: () => void;
  onRemoveFile?: (file: GlobalStorageFile) => void;
  options: QuillEditorOptions;
  quillRef: MutableRefObject<Quill | undefined>;
  setActiveMentionOptionId?: (id: string) => void;
  setDisplayLinkModal?: (display: boolean) => void;
  setDisplayVideoModal?: (display: boolean) => void;
  setForceEditorFocus?: (force: boolean) => void;
  setMentionsBoxId?: (id: string) => void;
  toggleEmojiPicker?: () => void;
  toolbarRef?: RefObject<HTMLDivElement>;
  uploads?: Array<GlobalStorageFile>;
  videos?: Array<GlobalStorageFile>;
}

interface QuillEditorProviderProps extends QuillEditorContextProps {
  children: React.ReactNode;
}

const QuillEditorContext = createContext({} as QuillEditorContextProps);

export const QuillEditorProvider = ({
  acceptAllFiles,
  attachments,
  children,
  compact,
  displayLinkModal,
  displayVideoModal,
  editorWrapperRef,
  files,
  format,
  id,
  images,
  initialEmbed,
  isEditorFocus,
  offEditorChange,
  onAddEmbed,
  onAddFiles,
  onAddLink,
  onEditorChange,
  onEditorEventsRef,
  onEmojiClick,
  onRemoveEmbed,
  onRemoveFile,
  options,
  quillRef,
  setActiveMentionOptionId,
  setDisplayLinkModal,
  setDisplayVideoModal,
  setForceEditorFocus,
  setMentionsBoxId,
  toggleEmojiPicker,
  toolbarRef,
  uploads,
  videos,
}: QuillEditorProviderProps) => {
  const value = useMemo<QuillEditorContextProps>(
    () => ({
      acceptAllFiles,
      attachments,
      compact,
      displayLinkModal,
      displayVideoModal,
      editorWrapperRef,
      files,
      format,
      id,
      images,
      initialEmbed,
      isEditorFocus,
      offEditorChange,
      onAddEmbed,
      onAddFiles,
      onAddLink,
      onEditorChange,
      onEditorEventsRef,
      onEmojiClick,
      onRemoveEmbed,
      onRemoveFile,
      options,
      quillRef,
      setActiveMentionOptionId,
      setDisplayLinkModal,
      setDisplayVideoModal,
      setForceEditorFocus,
      setMentionsBoxId,
      toggleEmojiPicker,
      toolbarRef,
      uploads,
      videos,
    }),
    [
      acceptAllFiles,
      attachments,
      compact,
      displayLinkModal,
      displayVideoModal,
      editorWrapperRef,
      files,
      format,
      id,
      images,
      initialEmbed,
      isEditorFocus,
      offEditorChange,
      onAddEmbed,
      onAddFiles,
      onAddLink,
      onEditorChange,
      onEditorEventsRef,
      onEmojiClick,
      onRemoveEmbed,
      onRemoveFile,
      options,
      quillRef,
      setActiveMentionOptionId,
      setDisplayLinkModal,
      setDisplayVideoModal,
      setForceEditorFocus,
      setMentionsBoxId,
      toggleEmojiPicker,
      toolbarRef,
      uploads,
      videos,
    ],
  );

  return (
    <QuillEditorContext.Provider value={value}>
      {children}
    </QuillEditorContext.Provider>
  );
};

export const useQuillEditorContext = () => useContext(QuillEditorContext);

export default QuillEditorContext;

export interface Quill extends QuillType {
  container: HTMLDivElement;
}
